import {
  Button, Descriptions, Divider, Edit,
  Empty,
  Form, Icons, RefreshButton, SaveButton, ShowButton, Space, Steps, Typography, useStepsForm
} from "@pankod/refine-antd";
import { IResourceComponentsProps } from "@pankod/refine-core";
import { RenderGroup } from "components/evaluation/renderGroup";
import { StateButtons } from "components/common";
import { CancelButton } from "components/utils/cancelButton";
import { DisplayScore } from "components/utils/displayScore";
import { LookupButton } from "components/utils/lookupButton";
import { ObjectMetadata } from "components/utils/objectMetadata";
import { IBaseProps, IEvaluation, IEvaluationModel } from "interfaces";
import { calculateScore, DATAPROVIDER_UPDATE, EVALUATIONMODEL_AGGREGATION_MIN, RESOURCE_PATH } from "scripts/site";
import { arrayExtensions } from "@architecture-innovation-transformation/lib-common";

export const EvaluationEdit: React.FC<IResourceComponentsProps> = () => {
  const {
    current,
    gotoStep,
    stepsProps,
    formProps,
    saveButtonProps,
    queryResult,
    submit
  } = useStepsForm<IEvaluation>({
    dataProviderName: DATAPROVIDER_UPDATE,
    isBackValidate: true,
    submitOnEnter: true,
    redirect: false
  });

  const record = queryResult?.data?.data;

  const validateEvaluationForm = () => {
    const values = formProps?.form?.getFieldsValue(true) || null;
    let isValid = true;
    
    // Validate the form if form values are available and validation is set to true
    if (values && (record?.evaluationModel?.aggregation !== EVALUATIONMODEL_AGGREGATION_MIN)) {
      var modelValues = values.evaluationModel as IEvaluationModel;
      modelValues.groups.forEach(grp => {
        grp.questions.forEach(ques => {
          var selectedOption = ques.multiSelect ?
            ques.options.filter(o => o.checked) :
            ques.options.filter(o => o.name === ques.selectedOption);
          if (!arrayExtensions.validateArray(selectedOption)) {
            isValid = false;
          }
        });
      });
    }
    return isValid;
  }

  return (
    <Edit {...saveButtonProps}
      isLoading={queryResult?.isFetching}
      headerProps={{
        title: "Evaluation",
        className: "frm-wt-st-btn"
      }
      }
      headerButtons={
        <>
          <ShowButton></ShowButton>
          <RefreshButton></RefreshButton>
        </>
      }
      footerButtons={
        <>
          {
            queryResult?.isFetching ? "" :
              <Space className="stt-frm-btn-st" wrap align="start">
                <StateButtons {...record as IBaseProps}
                  resourceToAct={RESOURCE_PATH.EVALUATION}
                  resourceToActId={record?.id || "0"}
                  navigateToId={(record?.engagementId || "0")}
                  resourceToNavigate={RESOURCE_PATH.ENGAGEMENTS}
                  hideActions={['reassign']}
                  fetchAssignedToList={true}
                  validateBusinessRules={validateEvaluationForm}
                />
              </Space>
          }
          <Space className="stt-frm-btn-frm" wrap align="end">
            <Button
              disabled={!(current > 0)}
              onClick={() => {
                gotoStep(current - 1);
              }}
            >
              <Icons.LeftOutlined />
              Previous
            </Button>
            <Button
              disabled={!(current < (record?.evaluationModel?.groups.length || 1) - 1)}
              onClick={() => {
                gotoStep(current + 1);
              }}
            >
              Next
              <Icons.RightOutlined />
            </Button>
            <SaveButton {...saveButtonProps} onClick={() => submit()} />
            <CancelButton />
          </Space>
        </>
      }
    >
      {
        queryResult?.isFetching ? "" :
          <>
            <Divider />
            <Descriptions style={{ marginBottom: 25 }} labelStyle={{ fontWeight: "bold" }} size="small">
              <Descriptions.Item label="Engagement">
                <LookupButton id={record?.engagementId || ""} resource={RESOURCE_PATH.ENGAGEMENTS}></LookupButton>
              </Descriptions.Item>
              <Descriptions.Item label="Evaluation Model">
                <LookupButton id={record?.evaluationModelId || ""} resource={RESOURCE_PATH.EVALUATIONMODEL}></LookupButton>
              </Descriptions.Item>
              <Descriptions.Item label={record?.evaluationModel?.ui.fieldLabel}>
                <DisplayScore
                  ui={record?.evaluationModel?.ui || "" as any}
                  output={record?.evaluationModel?.output || "" as any}
                />
              </Descriptions.Item>
              <Descriptions.Item label="">
                <ObjectMetadata {...record as IBaseProps} />
              </Descriptions.Item>
            </Descriptions>
            <Divider />
          </>
      }

      <Steps responsive {...stepsProps} percent={((current + 1) / (record?.evaluationModel.groups.length || 1)) * 100}
      >
        {record?.evaluationModel?.groups
          .sort(g => g.order)
          .map((g, i) => {
            return (
              <Steps.Step key={g.id} title={g.name}
                description={
                  record?.evaluationModel?.ui.displayCategoryScores ?
                    <>
                      <Typography.Text strong>Score: </Typography.Text>
                      <DisplayScore
                        override={true}
                        overrideScore={(record?.evaluationModel?.groups[i].score || g.score || 0)}
                        ui={record?.evaluationModel?.ui}
                        output={record?.evaluationModel?.output}
                      />
                    </>
                    : ""
                } />
            );
          })}
      </Steps>
      <Form {...formProps} autoComplete="off"
        layout="vertical"
      >
        {record
          ?
          <Form.Item
            name={["evaluationModel"]}
            rules={[
              {
                validator: (_rule, value: IEvaluationModel) => {
                  calculateScore(value);
                  return Promise.resolve();
                }
              }
            ]}
          >
            <RenderGroup {...record.evaluationModel.groups[current]} index={current} />
          </Form.Item>
          : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        }
      </Form>
    </Edit >
  );
};
